import React, { useEffect, useState, useCallback } from "react";
import { Button, Loader, Grid } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { DidacticMaterialsAPI, VirtualScenes } from "../../../../../api";
import { UserFreeSalasItemSalas } from "../UserFreeSalasItemSalas";
import { useLocaleStore } from "../../../../../store/localeStore";
import "./UserFreeSalasList.scss";

const didacticMaterialsController = new DidacticMaterialsAPI();
const virtualScenesController = new VirtualScenes();

export function UserFreeSalasList({
  accessToken,
  user,
  currentPage,
  onPageChange,
  onTotalPagesChange,
  reload,
  onReload,
}) {
  const [categories, setCategories] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(
    localStorage.getItem("selectedCategory") || null,
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    localStorage.getItem("selectedSubcategory") || null,
  );

  const { t } = useTranslation("scenes");
  const locale = useLocaleStore((s) => s.locale);

  const saveFiltersToLocalStorage = (category, subcategory) => {
    if (category !== undefined) {
      setSelectedCategory(category);
      localStorage.setItem("selectedCategory", category || "");
    }
    if (subcategory !== undefined) {
      setSelectedSubcategory(subcategory);
      localStorage.setItem("selectedSubcategory", subcategory || "");
    }
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setDataLoaded(false);
    try {
      const region = "Europe";
      const response = await didacticMaterialsController.getMaterialsByRegion(
        accessToken,
        region,
      );
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error("Error al cargar categorías:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, locale]); // locale triggers re-fetch on language switch via apiFetch

  const fetchScenes = useCallback(
    async (page = 1) => {
      setLoading(true);
      setDataLoaded(false);
      try {
        const response = await virtualScenesController.getVirtualScenesMiniFree(
          { page, limit: 50 },
          accessToken,
        );
        let allScenes = response.miniatureData || [];

        if (selectedCategory) {
          allScenes = allScenes.filter((scene) =>
            scene.category.includes(selectedCategory),
          );
        }
        if (selectedSubcategory) {
          allScenes = allScenes.filter((scene) =>
            scene.subcategory.includes(selectedSubcategory),
          );
        }

        setScenes(allScenes);
        onTotalPagesChange(response.totalPages || 1);

        // Pre-cargar imágenes para mejorar la experiencia de usuario
        await Promise.all(
          allScenes.map((scene) => {
            return new Promise((resolve) => {
              const img = new Image();
              img.src = scene.bannertopUrl;
              img.onload = resolve;
              img.onerror = resolve;
            });
          }),
        );
        setDataLoaded(true);
      } catch (error) {
        console.error("Error al cargar escenas:", error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      accessToken,
      selectedCategory,
      selectedSubcategory,
      onTotalPagesChange,
      locale, // locale triggers re-fetch on language switch via apiFetch
    ],
  );

  useEffect(() => {
    fetchCategories();
    fetchScenes(currentPage);
  }, [fetchCategories, fetchScenes, currentPage]);

  // Definimos una función para el botón de "upload" (si no se necesita, puede ser una función vacía)
  const handleUploadScene = (scene) => {
    // Implementa la funcionalidad que desees para "upload" o deja esta función vacía
    console.log("Subir escena:", scene);
  };

  const handleAllScenes = () => {
    saveFiltersToLocalStorage(null, null);
    fetchScenes();
    onPageChange(1);
  };

  const handleSelect = (category = null, subcategory = null) => {
    saveFiltersToLocalStorage(category, subcategory);
    fetchScenes();
    onPageChange(1);
  };

  return (
    <div className="salas-list">
      {loading || !dataLoaded ? (
        <Loader active inline="centered">
          {t("catalog.loadingData")}
        </Loader>
      ) : (
        <>
          <div className="salas-list__buttons">
            <Button
              onClick={handleAllScenes}
              active={!selectedCategory && !selectedSubcategory}
              className="btn-filter"
            >
              {t("catalog.allButton")}
            </Button>

            {categories.map((cat) => (
              <Button
                key={cat._id}
                onClick={() => handleSelect(cat.nameRaw, null)}
                active={selectedCategory === cat.nameRaw}
                className="btn-filter"
              >
                <div className="btn-filter__image-container">
                  <img
                    src={cat.avatar}
                    alt="avatar"
                    className="btn-filter__image"
                  />
                </div>
                <div className="btn-filter__text">{cat.name}</div>
              </Button>
            ))}

            <Button
              onClick={() => {
                console.log("Otros seleccionados");
                handleSelect("OTROS", null);
              }}
              active={selectedCategory === "OTROS"}
              className="btn-filter"
            >
              {t("catalog.othersButton")}
            </Button>
          </div>

          {selectedCategory && (
            <div className="salas-list__subcategories">
              {categories
                .find((cat) => cat.nameRaw === selectedCategory)
                ?.subcategories.map((sub) => (
                  <Button
                    key={sub._id}
                    onClick={() => handleSelect(selectedCategory, sub.nameRaw)}
                    active={selectedSubcategory === sub.nameRaw}
                    className="btn-subfilter"
                  >
                    <div className="btn-filter__image-container">
                      <img
                        src={sub.avatar || "https://via.placeholder.com/50"}
                        alt="subcat"
                        className="btn-filter__image"
                      />
                    </div>
                    <div className="btn-filter__text">{sub.name}</div>
                  </Button>
                ))}
            </div>
          )}

          <Grid stackable columns={6} className="salas-list__scenes">
            {scenes.length > 0 ? (
              scenes.map((scene) => (
                <Grid.Column
                  className="salas-list__scenes__salascolumn"
                  key={scene.refId}
                >
                  <UserFreeSalasItemSalas
                    scene={scene}
                    user={user}
                    onUploadScene={handleUploadScene}
                  />
                </Grid.Column>
              ))
            ) : (
              <p>{t("catalog.noScenesForSelection")}</p>
            )}
          </Grid>
        </>
      )}
    </div>
  );
}
